import { useEffect, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/ExpenseCategorySlice";
import { RootState } from "src/redux/store";
import {
  useDeleteExpenseCategoryMutation,
  useGetExpenseCategoryQuery,
} from "src/services/ExpenseCategoryServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddExpenseCategoriesFormWrapper from "../Add/AddExpenseCategoriesFormWrapper";
import EditExpenseCategoriesFormWrapper from "../Edit/EditExpenseCategoriesFormWrapper";
import ExpenseCategoryListing from "./ExpenseCategoryListing";
import { UserModuleActionTypes, UserModuleNameTypes } from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const ExpenseCategoryListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.expenseCategory);
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const [deleteExpenseCategory] = useDeleteExpenseCategoryMutation();
  const { data, isLoading, isFetching } = useGetExpenseCategoryQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["categoryName"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  const getActionOptions = (categoryId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setIsOpenEditForm(true);
          setSelectedCategoryId(categoryId);
        },
        moduleName: UserModuleNameTypes.EXPENSE_CATEGORIES,
        actionName: UserModuleActionTypes.EDIT,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure, you want to Delete this Category ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteExpenseCategory({ id: categoryId }).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        moduleName: UserModuleNameTypes.EXPENSE_CATEGORIES,
        actionName: UserModuleActionTypes.DELETE,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "categoryName",
      headerName: "Category Name",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[0.25_0.25_0%]",
      extraClasses: "",
      renderCell: (row) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  return (
    <div>
      <ExpenseCategoryListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.EXPENSE_CATEGORIES,
          actionName: UserModuleActionTypes.LIST,
          columns: columns
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddForm ? (
        <AddExpenseCategoriesFormWrapper
          onClose={() => setIsOpenAddForm(false)}
        />
      ) : null}
      {isOpenEditForm ? (
        <EditExpenseCategoriesFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          categoryId={selectedCategoryId}
        />
      ) : null}
    </div>
  );
};

export default ExpenseCategoryListingWrapper;
